import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {
  CheckCircle,
  ArrowRight,
  Shield,
  Award,
  Clock,
  Users,
  Heart,
  TrendingUp,
  HelpCircle
} from 'lucide-react'

import PageLayout from '../components/layout/PageLayout'

// Simple Badge component since it wasn't in original imports
const Badge = ({ children, className = "", variant = "default" }: { children: React.ReactNode, className?: string, variant?: string }) => {
  const baseStyles = "px-2.5 py-0.5 rounded-full text-xs font-semibold"
  const variants: any = {
    default: "bg-theme-primary/10 text-theme-primary",
    outline: "border border-theme-border text-theme-text-secondary",
    success: "bg-emerald-100 text-theme-success"
  }
  return <span className={`${baseStyles} ${variants[variant] || variants.default} ${className}`}>{children}</span>
}

const Pricing: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<'core_labs_os' | 'insight_os' | 'metabolic_os' | 'performance_os' | 'longevity_os'>('metabolic_os')

  const plans = {
    core_labs_os: {
      id: 'core_labs_os',
      name: 'Core Labs OS',
      price: '$149',
      period: 'per month',
      persona: 'For health-curious adults who want monthly baseline monitoring.',
      description: 'Monthly labs (20+ biomarkers), AI Health OS baseline, and Social OS access.',
      osCoverage: ['Lab OS', 'AI OS', 'Social OS', 'Wearable OS (1)'],
      features: [
        'Monthly Cardiometabolic Baseline (20+ Biomarkers)',
        'Biotune Score & Biological Age Tracking',
        'Abnormal Result Real-time Alerts',
        'AI Health OS Dashboard Access',
        'Verified Community Feed (Social OS)',
        '1 Genomic File Upload Included',
        '1 Wearable Device Connection (Apple/Fit)',
        'AI Recipe Intelligence (Generic)',
        'Digital Recipe Book & Macronutrient Planner',
      ],
      popular: false,
      savings: '',
      color: 'from-teal-500 to-emerald-500'
    },
    insight_os: {
      id: 'insight_os',
      name: 'Insight OS',
      price: '$249',
      period: 'per month',
      persona: 'For quantified-self builders needing deeper data integration.',
      description: 'Expanded labs (45+ biomarkers), advanced wearable sync, and Movement OS foundations.',
      osCoverage: ['Lab OS', 'AI OS', 'Movement OS', 'Social OS', 'Wearable OS (3)'],
      features: [
        'Everything in Core Labs OS',
        'Expanded Nutrient Status Panel (45+ Biomarkers)',
        'Advanced Inflammation & Cardiovascular Markers',
        'Unlimited Genomic File Uploads',
        'Up to 3 Wearable Device Connections',
        'Basic Wearable Performance Tracking',
        'Movement OS: Meditation & Breathwork Library',
        'Adherence & Milestone Streak Tracking',
        'Priority AI Insights Support',
      ],
      popular: false,
      savings: '',
      color: 'from-blue-500 to-indigo-500'
    },
    metabolic_os: {
      id: 'metabolic_os',
      name: 'Metabolic OS',
      price: '$399',
      period: 'per month',
      persona: 'For members optimizing metabolism, weight, and daily energy.',
      description: 'Precision Food OS (Bio-synced Recipes), Bio-synced Movement, and Mealmate delivery access.',
      osCoverage: ['Lab OS', 'AI OS', 'Food OS', 'Movement OS', 'Wearable OS (5)'],
      features: [
        'Everything in Insight OS',
        'Metabolic & Organ Function Panels (60+ Biomarkers)',
        'CGM (Continuous Glucose Monitor) Integration',
        'Up to 5 Wearable Device Connections',
        'Bio-Synced Meal Timers (Wearable Driven)',
        'Bio-Adaptive Recovery & Strain Insights',
        'Precision AI Recipes (Bio-tuned)',
        'Access to Mealmate Partner Deliveries (Add-on)',
        'Access to Balanced Meal Boxes (Add-on)',
      ],
      popular: true,
      savings: 'Best Seller',
      color: 'from-emerald-500 to-teal-600'
    },
    performance_os: {
      id: 'performance_os',
      name: 'Performance OS',
      price: '$699',
      period: 'per month',
      persona: 'For athletes and high-performers optimizing output and recovery.',
      description: 'Hormonal & Performance labs, Supplement OS, and Advanced Genetic screening.',
      osCoverage: ['Lab OS', 'AI OS', 'Food OS', 'Supplement OS', 'Wearable OS (Unltd)'],
      features: [
        'Everything in Metabolic OS',
        'Hormone Optimization Panel (80+ Biomarkers)',
        'Unlimited Wearable Device Support',
        'Professional Sleep & Stress OS Audit',
        'Pharmacogenomics (Drug-Gene Compatibility)',
        'Genetic Health Risk Analysis (100+ conditions)',
        'Precision Supplement Recommendations',
        'Adaptive Movement Intensity (Daily Bio-sync)',
        '2 Telehealth Performance Consults / year',
      ],
      popular: false,
      savings: '',
      color: 'from-purple-500 to-violet-600'
    },
    longevity_os: {
      id: 'longevity_os',
      name: 'Longevity OS',
      price: '$999',
      period: 'per month',
      persona: 'For longevity maximalists and executives wanting concierge Health OS.',
      description: 'Advanced longevity diagnostics, concierge travel coordination, and 24/7 care.',
      osCoverage: ['Lab OS', 'AI OS', 'Food OS', 'Movement OS', 'Wearable OS (Eco)', 'Concierge'],
      features: [
        'Everything in Performance OS',
        'Longevity, DNA & Toxin Screening (100+ Biomarkers)',
        'Full Ecosystem Wearable Data Audit (Monthly)',
        'Shotgun Metagenomic Microbiome Analysis',
        'Concierge Travel Food Coordination (Flight/Hotel)',
        'Custom Personalized Supplement Formulations',
        'Unlimited Telehealth Consultations',
        '24/7 Dedicated Concierge Health Team',
        'Insurance Claim & Reimbursement Support',
      ],
      popular: false,
      savings: 'Executive Concierge',
      color: 'from-amber-500 to-orange-600'
    },
  } as const

  const additionalFeatures = [
    {
      icon: Shield,
      title: "CLIA Certified Labs",
      description: "All testing performed in certified partner laboratories"
    },
    {
      icon: Award,
      title: "HIPAA-Aligned",
      description: "Your health data is encrypted and secure"
    },
    {
      icon: Clock,
      title: "Monthly Diagnostics",
      description: "A continuous feedback loop with monthly lab rhythms"
    },
    {
      icon: Users,
      title: "Clinical Specialists",
      description: "Direct access to Dietitians and Performance coaches"
    },
    {
      icon: Heart,
      title: "HSA/FSA Eligible",
      description: "Use your health savings for all lab components"
    },
    {
      icon: TrendingUp,
      title: "30-Day Commitment",
      description: "No free trials. Transparent, proactive healthcare from day one."
    }
  ]

  const faqs = [
    {
      question: "What is Wearable OS?",
      answer: "Wearable OS is our proprietary data engine that syncs with Apple Health, Google Fit, Oura, Whoop, and Garmin. It uses your real-time performance data to adjust your Movement OS intensity and Food OS meal timers daily."
    },
    {
      question: "What is the difference between Recipes and Meal Boxes?",
      answer:
        "Every tier includes our AI Recipe Engine for free. Meal Boxes (ingredients) and Mealmate Restaurant Delivery (prepared) are physical fulfillment services available as paid add-ons to any tier.",
    },
    {
      question: "How does 'Bio-Synced Meal Timing' work?",
      answer: "Starting with Metabolic OS, our Wearable OS monitors your activity levels and glucose trends (if using CGM) to send you real-time alerts on the best windows to eat for optimal metabolic response."
    },
    {
      question: "Internal Lab OS vs Specialty Testing?",
      answer: "Lab OS covers our core panels. Longevity OS includes specialty tests like Tumor Markers and Heavy Metals which are typically extra-cost diagnostics."
    },
    {
      question: "Can I cancel easily?",
      answer: "Yes. After your first 30-day billing period, you can cancel at any time. We bill monthly upfront to ensure we can schedule your phlebotomy and lab processing."
    }
  ]

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-theme-surface to-theme-bg overflow-hidden relative pt-12">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center mb-6">
              <Badge variant="outline" className="py-1 px-4 border-theme-primary/30 text-theme-primary bg-theme-primary/5">
                Health OS Ecosystem
              </Badge>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold tracking-tight mb-6">
              Precision Health <br />
              <span className="bg-gradient-to-r from-blue-600 via-teal-500 to-indigo-600 bg-clip-text text-transparent">
                Without the Guesswork
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-theme-secondary mb-10 leading-relaxed font-light">
              Monthly diagnostics. Wearable performance tracking. Bio-synced nutrition. <br className="hidden md:block" />
              Choose the Health OS that powers your life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth/signup">
                <Button size="lg" className="bg-theme-primary hover:bg-theme-primary/90 text-theme-text-inverse rounded-full px-8 h-14 text-lg shadow-xl shadow-theme-primary/20">
                  Select Your Plan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/features">
                <Button size="lg" variant="outline" className="rounded-full h-14 px-8 border-theme-border hover:bg-theme-surface">
                  Compare Every Feature
                </Button>
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Plan Selector Tab-like interface for mobile/tablet */}
      <section className="sticky top-[80px] z-40 py-4 bg-theme-bg/80 backdrop-blur-xl border-y border-theme-border lg:hidden">
        <Container>
          <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide no-scrollbar">
            {Object.values(plans).map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedPlan === plan.id
                    ? 'bg-theme-primary text-theme-text-inverse shadow-lg'
                    : 'bg-theme-surface text-theme-text-secondary border border-theme-border'
                }`}
              >
                {plan.name}
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* Desktop Grid / Mobile Selected Plan View */}
      <section className="section-padding relative">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {Object.values(plans).map((plan) => {
              const isActive = selectedPlan === plan.id
              return (
                <motion.div
                  key={plan.id}
                  layout
                  className={`relative group ${!isActive ? 'hidden lg:block opacity-60 hover:opacity-100' : 'block'}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: isActive ? 1.05 : 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <Card className={`h-full border border-theme-border shadow-lg hover:shadow-xl overflow-hidden bg-theme-surface/50 backdrop-blur-sm transition-all duration-500 ${
                    isActive ? 'ring-2 ring-theme-primary/50' : ''
                  }`}>
                    {/* Header with Tier Color */}
                    <div className={`h-2 bg-gradient-to-r ${plan.color}`} />
                    
                    <CardHeader className="pb-8">
                      <div className="flex justify-between items-start mb-4">
                        <CardTitle className="text-xl font-bold">{plan.name}</CardTitle>
                        {plan.popular && (
                          <Badge variant="default" className="bg-theme-primary text-theme-text-inverse border-0">Popular</Badge>
                        )}
                      </div>
                      <div className="flex items-baseline mb-4">
                        <span className="text-4xl font-black">{plan.price}</span>
                        <span className="text-theme-secondary text-sm ml-1">/mo</span>
                      </div>
                      <p className="text-sm text-theme-secondary leading-relaxed h-12 overflow-hidden">
                        {plan.persona}
                      </p>
                    </CardHeader>

                    <CardContent className="space-y-6">
                      <div className="pt-6 border-t border-theme-border">
                        <p className="text-[10px] font-black uppercase tracking-widest text-theme-primary mb-4">
                          Included in {plan.name}
                        </p>
                        <ul className="space-y-4">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-theme-secondary leading-snug">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Link to={`/auth/signup?plan=${plan.id}`} className="block pt-8">
                        <Button 
                          className={`w-full h-12 rounded-xl font-bold transition-all ${
                            isActive 
                              ? `bg-gradient-to-r ${plan.color} text-white shadow-lg` 
                              : 'bg-theme-surface text-theme-primary border border-theme-border hover:bg-theme-bg'
                          }`}
                        >
                          Get Started
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              )
            })}
          </div>
        </Container>
      </section>

      {/* Wearable OS & Food OS Modular Model Demo */}
      <section className="section-padding bg-theme-surface/30 border-y border-theme-border overflow-hidden">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="mb-4">
                <Badge variant="success" className="bg-theme-success/10 text-theme-success hover:bg-theme-success/20 border-0">
                  Wearable OS Integration
                </Badge>
              </div>
              <h2 className="text-4xl font-bold mb-6">Performance Tracking Meets Bio-Synced Meal Plans</h2>
              <p className="text-lg text-theme-secondary mb-8 leading-relaxed">
                Connect your devices to the BIOTUNE ecosystem. Our Wearable OS tracks your daily strain, recovery, and movement to automate your meal windows and exercise intensity.
              </p>
              
              <div className="space-y-6">
                <div className="flex space-x-4 p-4 rounded-2xl bg-theme-surface border border-theme-border">
                  <div className="w-12 h-12 rounded-xl bg-theme-primary/5 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-theme-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold">Performance Tracking</h4>
                    <p className="text-sm text-theme-secondary">Real-time sync with Apple Health, Oura, Garmin, and more to monitor your athletic output.</p>
                  </div>
                </div>
                <div className="flex space-x-4 p-4 rounded-2xl bg-theme-surface border border-theme-border">
                  <div className="w-12 h-12 rounded-xl bg-theme-warning/5 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-theme-warning" />
                  </div>
                  <div>
                    <h4 className="font-bold">Bio-Synced Meal Timers</h4>
                    <p className="text-sm text-theme-secondary">Automated alerts telling you exactly when to eat based on your wearable performance data.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-theme-primary/10 to-theme-primary/20 p-8">
                <div className="w-full h-full rounded-2xl bg-theme-surface shadow-2xl p-6 relative overflow-hidden border border-theme-border">
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-bold text-lg text-theme-text-primary">Wearable OS: Live Sync</span>
                    <Badge variant="default" className="bg-theme-primary text-theme-text-inverse border-0">Connected</Badge>
                  </div>
                  <div className="space-y-6">
                    <div className="flex justify-between items-center text-sm text-theme-text-secondary">
                        <span>Strain Score</span>
                        <span className="font-bold">14.8/21</span>
                    </div>
                    <div className="h-2 w-full bg-theme-surface-light rounded-full overflow-hidden">
                        <div className="h-full w-[70%] bg-theme-primary" />
                    </div>
                    <div className="flex justify-between items-center text-sm text-theme-text-secondary">
                        <span>Recovery</span>
                        <span className="font-bold text-theme-success">82%</span>
                    </div>
                  </div>
                  <div className="mt-8 pt-8 border-t border-theme-border text-center">
                    <p className="text-xs text-theme-text-muted uppercase tracking-widest mb-4">Meal Tracking Suggestion</p>
                    <div className="py-3 px-4 rounded-xl bg-theme-warning/10 border border-theme-warning/20 text-theme-warning font-bold text-sm">
                        Optimal Fuel Window: 2:45 PM - 3:30 PM
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Trust & Compliance Grid */}
      <section className="section-padding">
        <Container>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
            {additionalFeatures.map((f, i) => (
              <div key={i} className="flex items-start space-x-6">
                <div className="w-12 h-12 rounded-xl bg-theme-surface border border-theme-border flex items-center justify-center flex-shrink-0">
                  <f.icon className="w-5 h-5 text-theme-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">{f.title}</h3>
                  <p className="text-sm text-theme-secondary leading-relaxed">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ Deep Dive */}
      <section className="section-padding bg-theme-surface/20">
        <Container>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-black mb-12 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <Card key={i} className="border-theme-border bg-theme-surface/50 hover:bg-theme-surface transition-colors">
                  <CardContent className="p-6">
                    <h4 className="font-bold mb-2 flex items-center text-theme-text-primary">
                      <HelpCircle className="w-5 h-5 text-theme-primary mr-3 flex-shrink-0" />
                      {faq.question}
                    </h4>
                    <p className="text-sm text-theme-secondary leading-relaxed pl-8">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="section-padding">
        <Container>
          <div className="bg-gradient-to-br from-blue-700 via-indigo-700 to-indigo-800 rounded-[48px] p-12 md:p-24 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-cover" />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-5xl md:text-6xl font-black mb-8">Take Control of Your <br /> Biological Future</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-12 leading-relaxed">
                Join thousands of members who have replaced "annual physicals" with continuous Health OS monitoring and data-driven optimization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth/signup">
                  <Button size="lg" className="bg-theme-surface text-theme-primary hover:bg-theme-surface-light rounded-full px-12 h-16 text-lg font-black shadow-2xl transition-all">
                    Get Started Now
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-12 h-16 text-lg">
                    Contact Concierge
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>
    </PageLayout>
  )
}

export default Pricing
